import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { getCookie, removeCookie, setCookie } from "@/cookies";
import { useDebounce } from "@/hooks/useDebounce";
import type { Audience, Sort } from "@/hooks/useFilters";
import { AppContext } from "./AppContext";

interface OpenDayContextProviderProps {
	children: React.ReactElement[] | React.ReactElement;
}

const storageKey = "CU" as const;
const audiendeStorageKey = `${storageKey}_audience` as const;

export default function AppContextProvider({
	children,
}: OpenDayContextProviderProps) {
	const [searchParams, setSearchParams] = useState(
		() => new URL(window.location.href).searchParams,
	);
	const [audience, setAudience] = useState<Audience | undefined>(() => {
		const audienceParam = searchParams.get("audience");

		if (
			audienceParam &&
			(audienceParam === "home" || audienceParam === "international")
		) {
			return audienceParam as Audience;
		} else {
			const backup = getCookie(audiendeStorageKey) as Audience | null;

			if (backup && (backup === "home" || backup === "international"))
				return backup ?? undefined;
		}
	});

	const [inputQuery, setInputQuery] = useState<string>(() => {
		const queryPraram = searchParams.get("q");

		return queryPraram ?? "";
	});

	const [query, setDebounedQuery] = useState<string>(() => {
		const queryPraram = searchParams.get("q");

		return queryPraram ?? "";
	});
	const handleChange = useCallback((val: string) => {
		const url = new URL(window.location.href);

		url.searchParams.set("q", val ?? "");
		window.history.pushState({}, "", url);
		setDebounedQuery(val);
	}, []);

	const debounce = useDebounce(handleChange);
	const [sort, setSort] = useState<Sort>(() => {
		const sortPraram = searchParams.get("sort") as Sort | null;

		return sortPraram ?? "a-z";
	});

	const handleQuery = useCallback(
		(query: string) => {
			setInputQuery(query);
			debounce(query);
		},
		[debounce],
	);

	useEffect(() => {
		if (!audience) {
			removeCookie(audiendeStorageKey);
			return;
		}
		setCookie(audiendeStorageKey, audience);
	}, [audience]);

	useEffect(() => {
		const onPopState = () => {
			const search = new URL(window.location.href).searchParams;
			const query = search.get("q") ?? "";
			setSearchParams(search);
			setInputQuery(query);
			setDebounedQuery(query);
		};

		window.addEventListener("popstate", onPopState);

		return () => {
			window.removeEventListener("popstate", onPopState);
		};
	}, []);

	useEffect(() => {
		const url = new URL(window.location.href);

		url.searchParams.set("sort", sort ?? "");
		window.history.pushState({}, "", url);
	}, [sort]);

	/**
	 * Return Context provider for any children to consume
	 */
	return (
		<AppContext.Provider
			value={{
				audience,
				setAudience,
				sort,
				setSort,
				inputQuery,
				query,
				handleQuery,
			}}
		>
			{children}
		</AppContext.Provider>
	);
}
